<template>
  <AsyncModal
    :fetch="fetchGoodestFinds"
    :name="MODAL_NAMES.HIGHLIGHTED_PICKERS"
    :title="modalTitle"
  >
    <template #trigger="{ open }">
      <div class="body-2-bold">
        <RevLink @click="open">
          {{ i18n(translations.modalTrigger) }}
        </RevLink>
      </div>
    </template>
    <template #content>
      <div v-if="pickers.length">
        <RevIllustration
          alt=""
          class="rounded-lg"
          :height="342"
          :src="selectedPickerIllustration"
          :width="342"
        />
        <div class="mt-24 grid grid-cols-2 gap-8">
          <RevPicker
            v-for="(picker, index) in pickers"
            :key="picker.label"
            :label="picker.label"
            :prefix="picker.icon"
            :selected="picker.selected"
            @click="handlePickersClick(index)"
          />
        </div>

        <div
          v-for="paragraph in selectedPickersParagraphs"
          :key="paragraph.title"
          class="mt-24"
          :class="{ 'md:hidden': paragraph.linkToApp }"
        >
          <h3 class="heading-3">
            {{ paragraph.title }}
          </h3>
          <p class="body-1 mt-8">
            {{ paragraph.text }}
          </p>
        </div>
      </div>
    </template>
  </AsyncModal>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'

import { productAPI } from '@backmarket/http-api'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevIllustration } from '@ds/components/Illustration'
import { RevLink } from '@ds/components/Link'
import { RevPicker } from '@ds/components/Picker'
import { IconEyes } from '@ds/icons/IconEyes'
import { IconSparkles } from '@ds/icons/IconSparkles'

import { MODAL_NAMES } from '~/scopes/product/constants'

import { useUrlParams } from '../../../../composables/useUrlParams'
import AsyncModal from '../../../AsyncModal/AsyncModal.vue'

import translations from './RecommendedModal.translations'

const i18n = useI18n()
const { productId } = useUrlParams()

const ILLUSTRATIONS: Record<string, string> = {
  IllustrationMoneyBagz: '/img/product/pickers/cheapest-offer.svg',
  IllustrationMoneyBagzSparkles: '/img/product/pickers/top-offer.svg',
}

const PICKER_ICONS: Record<string, unknown> = {
  IconEyes,
  IconSparkles,
}

const content = ref()
const selectedPickerId = ref(0)

const modalTitle = computed(() => {
  return content.value?.title ?? ''
})

const pickers = computed(() => {
  return content.value.buttons.map(
    (
      { label, icon }: productAPI.GetGoodestFindsResponse['buttons'][number],
      index: number,
    ) => ({
      label,
      icon: PICKER_ICONS[icon],
      selected: index === selectedPickerId.value,
    }),
  )
})

const selectedPickerIllustration = computed(() => {
  const img = content.value.buttons[selectedPickerId.value]?.image ?? null

  return ILLUSTRATIONS[img]
})

const selectedPickersParagraphs = computed(() => {
  return content.value.buttons[selectedPickerId.value]?.paragraphs ?? null
})

async function fetchGoodestFinds() {
  const { data } = await useHttpFetch(productAPI.getGoodestFinds, {
    pathParams: { productId },
  })
  content.value = { ...data.value }
}

function handlePickersClick(index: number) {
  selectedPickerId.value = index
}
</script>
